import React, {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {TranslationKey} from '../../../../../../../_metronic/i18n/translationKeys'
import {ClassOption, PersonOption} from '../../../../modes/attendance/attendanceV2Types'
import {AttendanceChatSendExtras} from '../../../../modes/attendance/attendanceWizardTypes'
import {
  buildClassDisambiguationPick,
  buildPersonDisambiguationPick,
} from '../../../../modes/attendance/buildDisambiguationPick'

const PAGE_SIZE = 8

type AttendanceV2DisambiguationPickerProps = {
  field: 'person' | 'class' | 'branch'
  options: PersonOption[] | ClassOption[]
  theme: 'light' | 'dark'
  onSelect: (query: string, extras?: AttendanceChatSendExtras) => void
}

function isPersonOption(opt: PersonOption | ClassOption): opt is PersonOption {
  return typeof (opt as PersonOption).name === 'string'
}

/** The dimension a non-person candidate belongs to, defaulted to `class` for the older flows that
 *  send no `kind`. */
function groupKindOf(kind?: string): 'class' | 'subject' | 'grade' | 'section' {
  const k = (kind || '').toLowerCase()
  if (k === 'subject' || k === 'grade' || k === 'section') return k
  return 'class'
}

const GROUP_ID_LABEL: Record<string, string> = {
  class: 'Class ID',
  subject: 'Subject ID',
  grade: 'Grade ID',
  section: 'Section ID',
}

function groupIdLabel(kind?: string): string {
  return GROUP_ID_LABEL[groupKindOf(kind)]
}

const PERSON_NOUN_KEY: Record<string, TranslationKey> = {
  student: 'CHATBOT.ATTENDANCE.NOUN_STUDENT',
  teacher: 'CHATBOT.ATTENDANCE.NOUN_TEACHER',
  staff: 'CHATBOT.ATTENDANCE.NOUN_STAFF',
}

const GROUP_NOUN_KEY: Record<string, TranslationKey> = {
  class: 'CHATBOT.ATTENDANCE.NOUN_CLASS',
  subject: 'CHATBOT.ATTENDANCE.NOUN_SUBJECT',
  grade: 'CHATBOT.ATTENDANCE.NOUN_GRADE',
  section: 'CHATBOT.ATTENDANCE.NOUN_SECTION',
}

/** What to CALL the things on offer, read from the candidates themselves.
 *
 *  The hint under the list was fixed text: "Or type a student name or id mateen_1 in the chat
 *  below." Asked about "teacher asma" the backend correctly offers two TEACHERS, and the line
 *  beneath them still asked for a student — the one QA report that came back on this picker more
 *  than any other. Mixed types fall back to the neutral "person": with a student and a teacher both
 *  on screen, neither noun is true of the list.
 */
function personNounKey(options: PersonOption[]): TranslationKey {
  const types = new Set(
    options.map((opt) => (opt.type || '').toLowerCase()).filter((type) => PERSON_NOUN_KEY[type])
  )
  if (types.size !== 1) return 'CHATBOT.ATTENDANCE.NOUN_PERSON'
  return PERSON_NOUN_KEY[[...types][0]]
}

function groupNounKey(options: ClassOption[]): TranslationKey {
  const kinds = new Set(options.map((opt) => groupKindOf(opt.kind)))
  if (kinds.size !== 1) return 'CHATBOT.ATTENDANCE.NOUN_CLASS'
  return GROUP_NOUN_KEY[[...kinds][0]]
}

export function personDisambiguationSubmitQuery(opt: PersonOption): string {
  // Prefer the id — the name is exactly the string the backend flagged as ambiguous, so resubmitting
  // it just re-triggers disambiguation. The backend resolves an `id <n>` query directly (per its own
  // "type a name or id …" hint). Falls back to the name when no id is available.
  const id = opt.studentId ?? opt.userId
  if (id != null) return `id ${id}`
  return opt.name?.trim() ?? ''
}

/** `submitQuery` wins when the backend supplied one. It is the same reasoning as
 *  `personDisambiguationSubmitQuery`: the label is exactly the string flagged as ambiguous, so
 *  resubmitting it re-triggers the same question. For a subject the backend sends
 *  "Economics (A2 (Com-Q)) — subject ID 76", which resolves twice over — the bracketed label finds
 *  the candidates, the id picks the one meant. Falls back to the label for the older class and
 *  branch flows, which are unambiguous by the time they reach here. */
function classSubmitQuery(opt: ClassOption): string {
  const explicit = opt.submitQuery?.trim()
  if (explicit) return explicit
  return (opt.label || opt.className || opt.gradeName || '').trim()
}

const AttendanceV2DisambiguationPicker: React.FC<AttendanceV2DisambiguationPickerProps> = ({
  field,
  options,
  theme,
  onSelect,
}) => {
  const intl = useIntl()
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const visibleOptions = useMemo(() => options.slice(0, visibleCount), [options, visibleCount])
  const hasMore = visibleCount < options.length
  const remaining = options.length - visibleCount

  if (!options.length) return null

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
  const hoverBg = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
  const textColor = theme === 'dark' ? '#ffffff' : '#212529'
  const mutedColor = theme === 'dark' ? 'rgba(255,255,255,0.65)' : '#6c757d'

  const firstPerson = options.find(isPersonOption)
  const isPersonField = field === 'person'
  const typeHintId: TranslationKey = isPersonField
    ? 'CHATBOT.ATTENDANCE.DISAMBIGUATION_TYPE_HINT_PERSON'
    : 'CHATBOT.ATTENDANCE.DISAMBIGUATION_TYPE_HINT_CLASS'
  // The noun the hint uses, taken from what is actually on offer rather than assumed to be a
  // student — see `personNounKey`.
  const noun = intl.formatMessage({
    id: isPersonField
      ? personNounKey(options as PersonOption[])
      : groupNounKey(options as ClassOption[]),
  })

  return (
    <div className='d-flex flex-column gap-2 mt-2'>
      {options.length > PAGE_SIZE ? (
        <div style={{fontSize: '0.75rem', color: mutedColor}}>
          {intl.formatMessage(
            {id: 'CHATBOT.ATTENDANCE.DISAMBIGUATION_SHOWING'},
            {shown: Math.min(visibleCount, options.length), total: options.length}
          )}
        </div>
      ) : null}

      {visibleOptions.map((opt, idx) => {
        const query =
          field === 'person' && isPersonOption(opt)
            ? personDisambiguationSubmitQuery(opt)
            : classSubmitQuery(opt as ClassOption)
        if (!query) return null

        let title = ''
        let subtitle = ''

        if (field === 'person' && isPersonOption(opt)) {
          const parts = [opt.name]
          if (opt.class) {
            parts.push(opt.section ? `${opt.class} (${opt.section})` : opt.class)
          }
          if (opt.branch) parts.push(opt.branch)
          title = parts.filter(Boolean).join(' · ')
          if (opt.studentId != null) subtitle = `ID: ${opt.studentId}`
        } else {
          const classOpt = opt as ClassOption
          // `qualifier` is the class a subject is taught to and `levelName` the level a grade or
          // section sits in — the only things separating two cards both reading "Economics" or
          // both reading "AS". Without them the reader is asked to choose between duplicates.
          title =
            classOpt.label ||
            [
              classOpt.gradeName || classOpt.className,
              classOpt.qualifier,
              classOpt.levelName,
              classOpt.section,
              classOpt.branch,
            ]
              .filter(Boolean)
              .join(' · ')
          const isSubject = classOpt.kind === 'subject'
          const id = isSubject ? (classOpt.subjectId ?? classOpt.classId) : classOpt.classId
          // Named by what it IS. A grade's id labelled "Class ID" sends the reader looking for a
          // class with that number, and the two id spaces are unrelated.
          subtitle = id != null ? `${groupIdLabel(classOpt.kind)}: ${id}` : ''
        }

        return (
          <button
            key={`${query}-${idx}`}
            type='button'
            className='btn text-start w-100'
            style={{
              border: `1px solid ${borderColor}`,
              borderRadius: 10,
              padding: '10px 12px',
              color: textColor,
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = hoverBg
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
            onClick={() => {
              // `query` may be an `id <n>` string for exact resolution; show the readable label in
              // the user's bubble instead.
              const displayText = title || query
              if (field === 'person' && isPersonOption(opt)) {
                onSelect(query, {
                  disambiguationPick: buildPersonDisambiguationPick(opt),
                  displayText,
                })
                return
              }
              onSelect(query, {
                disambiguationPick: buildClassDisambiguationPick(opt as ClassOption),
                displayText,
              })
            }}
          >
            <div style={{fontWeight: 600, fontSize: '0.85rem'}}>{title}</div>
            {subtitle ? (
              <div style={{fontSize: '0.72rem', color: mutedColor, marginTop: 2}}>{subtitle}</div>
            ) : null}
          </button>
        )
      })}

      {hasMore ? (
        <button
          type='button'
          className='btn btn-sm btn-outline-primary align-self-start'
          onClick={() => setVisibleCount((count) => Math.min(count + PAGE_SIZE, options.length))}
        >
          {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.LOAD_MORE'})}
          {remaining > PAGE_SIZE ? ` (${PAGE_SIZE})` : ` (${remaining})`}
        </button>
      ) : null}

      <div style={{fontSize: '0.78rem', color: mutedColor, marginTop: 4}}>
        {isPersonField
          ? intl.formatMessage(
              {id: typeHintId},
              {
                noun,
                // A real id off the list beats an invented one: the reader can copy it, and it is
                // the token the backend resolves on.
                id: firstPerson?.studentId != null ? String(firstPerson.studentId) : '4234',
              }
            )
          : intl.formatMessage({id: typeHintId}, {noun})}
      </div>
    </div>
  )
}

export default AttendanceV2DisambiguationPicker
